import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {MessageService} from 'primeng/api';
import {Subscription} from 'rxjs';
import {CampaignBulkOperationService} from '../../service/api/campaign-bulk-operation.service';
import {
    BulkAssignRequest,
    BulkDoNotContactRequest,
    BulkOperationItemResponse,
    BulkOperationResponse,
    BulkRemovalRequest,
    BulkStatusChangeRequest
} from '../../model/dto/api/bulk-operation.interface';
import {CampaignMemberDto} from '../../model/dto/entity/campaign-member.dto';
import {BulkOperationTypeEnum} from '../../enum/bulk-operation-type.enum';
import {BulkOperationStatusEnum} from '../../enum/bulk-operation-status.enum';
import {BulkOperationItemStatusEnum} from '../../enum/bulk-operation-item-status.enum';

@Component({
    selector: 'ct-campaign-bulk-operations',
    templateUrl: './campaign-bulk-operations.component.html',
    styleUrls: ['./campaign-bulk-operations.component.scss'],
    standalone: false
})
export class CampaignBulkOperationsComponent implements OnInit, OnDestroy {

    @Input() campaignId: number;
    @Input() selectedMembers: CampaignMemberDto[] = [];
    @Input() statusTypes: { id: number, name: string }[] = [];
    @Input() agents: { id: number, name: string }[] = [];

    @Output() onOperationComplete = new EventEmitter<BulkOperationResponse>();

    // Operation history
    operationHistory: BulkOperationResponse[] = [];
    selectedOperation: BulkOperationResponse;
    operationItems: BulkOperationItemResponse[] = [];

    // Dialog states
    showStatusChangeDialog: boolean = false;
    showAssignDialog: boolean = false;
    showRemovalDialog: boolean = false;
    showDncDialog: boolean = false;
    showHistoryDialog: boolean = false;
    showItemsDialog: boolean = false;
    showFailedItemsDialog: boolean = false;

    // Form data
    selectedStatusType: { id: number, name: string };
    selectedAgent: { id: number, name: string };
    operationReason: string = '';

    // UI State
    busy: boolean = false;

    // Enums for template
    BulkOperationTypeEnum = BulkOperationTypeEnum;
    BulkOperationStatusEnum = BulkOperationStatusEnum;
    BulkOperationItemStatusEnum = BulkOperationItemStatusEnum;

    private subscriptions: Subscription[] = [];

    constructor(
        private bulkOperationService: CampaignBulkOperationService,
        private messageService: MessageService
    ) {
    }

    ngOnInit(): void {
        this.loadOperationHistory();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    loadOperationHistory(): void {
        if (!this.campaignId) return;

        this.busy = true;
        const sub = this.bulkOperationService.getOperationsForCampaign(this.campaignId).subscribe({
            next: (operations) => {
                this.operationHistory = operations;
                this.busy = false;
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Fehler',
                    detail: 'Operationsverlauf konnte nicht geladen werden'
                });
                this.busy = false;
            }
        });
        this.subscriptions.push(sub);
    }

    // Dialog openers
    openStatusChangeDialog(): void {
        if (!this.validateSelection()) return;
        this.resetForm();
        this.showStatusChangeDialog = true;
    }

    openAssignDialog(): void {
        if (!this.validateSelection()) return;
        this.resetForm();
        this.showAssignDialog = true;
    }

    openRemovalDialog(): void {
        if (!this.validateSelection()) return;
        this.resetForm();
        this.showRemovalDialog = true;
    }

    openDncDialog(): void {
        if (!this.validateSelection()) return;
        this.resetForm();
        this.showDncDialog = true;
    }

    openHistoryDialog(): void {
        this.loadOperationHistory();
        this.showHistoryDialog = true;
    }

    // Execute operations
    executeBulkStatusChange(): void {
        if (!this.selectedStatusType) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Kein Status ausgewählt',
                detail: 'Bitte wählen Sie einen Status zum Anwenden aus'
            });
            return;
        }

        this.busy = true;
        const request: BulkStatusChangeRequest = {
            memberIds: this.selectedMembers.map(m => m.id),
            newStatusTypeId: this.selectedStatusType.id,
            reason: this.operationReason
        };

        const sub = this.bulkOperationService.bulkStatusChange(this.campaignId, request).subscribe({
            next: (response) => {
                this.handleOperationResponse(response, 'Statusänderung');
                this.showStatusChangeDialog = false;
            },
            error: (error) => {
                this.handleOperationError('Statusänderung');
            }
        });
        this.subscriptions.push(sub);
    }

    executeBulkAssign(): void {
        if (!this.selectedAgent) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Kein Agent ausgewählt',
                detail: 'Bitte wählen Sie einen Agenten für die Zuweisung aus'
            });
            return;
        }

        this.busy = true;
        const request: BulkAssignRequest = {
            memberIds: this.selectedMembers.map(m => m.id),
            agentId: this.selectedAgent.id,
            reason: this.operationReason
        };

        const sub = this.bulkOperationService.bulkAssign(this.campaignId, request).subscribe({
            next: (response) => {
                this.handleOperationResponse(response, 'Zuweisung');
                this.showAssignDialog = false;
            },
            error: (error) => {
                this.handleOperationError('Zuweisung');
            }
        });
        this.subscriptions.push(sub);
    }

    executeBulkRemoval(): void {
        this.busy = true;
        const request: BulkRemovalRequest = {
            memberIds: this.selectedMembers.map(m => m.id),
            reason: this.operationReason
        };

        const sub = this.bulkOperationService.bulkRemove(this.campaignId, request).subscribe({
            next: (response) => {
                this.handleOperationResponse(response, 'Entfernung');
                this.showRemovalDialog = false;
            },
            error: (error) => {
                this.handleOperationError('Entfernung');
            }
        });
        this.subscriptions.push(sub);
    }

    executeBulkDnc(): void {
        this.busy = true;
        const request: BulkDoNotContactRequest = {
            memberIds: this.selectedMembers.map(m => m.id),
            reason: this.operationReason
        };

        const sub = this.bulkOperationService.bulkDoNotContact(this.campaignId, request).subscribe({
            next: (response) => {
                this.handleOperationResponse(response, 'Nicht kontaktieren');
                this.showDncDialog = false;
            },
            error: (error) => {
                this.handleOperationError('Nicht kontaktieren');
            }
        });
        this.subscriptions.push(sub);
    }

    // View operation details
    viewOperationItems(operation: BulkOperationResponse): void {
        this.selectedOperation = operation;
        this.busy = true;

        const sub = this.bulkOperationService.getOperationItems(operation.id).subscribe({
            next: (items) => {
                this.operationItems = items;
                this.showItemsDialog = true;
                this.busy = false;
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Fehler',
                    detail: 'Operationseinträge konnten nicht geladen werden'
                });
                this.busy = false;
            }
        });
        this.subscriptions.push(sub);
    }

    viewFailedItems(operation: BulkOperationResponse): void {
        this.selectedOperation = operation;
        this.busy = true;

        const sub = this.bulkOperationService.getFailedItems(operation.id).subscribe({
            next: (items) => {
                this.operationItems = items;
                this.showFailedItemsDialog = true;
                this.busy = false;
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Fehler',
                    detail: 'Fehlgeschlagene Einträge konnten nicht geladen werden'
                });
                this.busy = false;
            }
        });
        this.subscriptions.push(sub);
    }

    cancelOperation(operation: BulkOperationResponse): void {
        this.busy = true;

        const sub = this.bulkOperationService.cancelOperation(operation.id).subscribe({
            next: (response) => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Abgebrochen',
                    detail: 'Operation erfolgreich abgebrochen'
                });
                this.loadOperationHistory();
                this.busy = false;
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Fehler',
                    detail: 'Operation konnte nicht abgebrochen werden'
                });
                this.busy = false;
            }
        });
        this.subscriptions.push(sub);
    }

    // Helper methods
    private validateSelection(): boolean {
        if (this.selectedMembers.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Keine Auswahl',
                detail: 'Bitte wählen Sie Mitglieder für Massen-Operationen aus'
            });
            return false;
        }
        return true;
    }

    private resetForm(): void {
        this.selectedStatusType = null;
        this.selectedAgent = null;
        this.operationReason = '';
    }

    private handleOperationResponse(response: BulkOperationResponse, operationType: string): void {
        this.busy = false;
        this.loadOperationHistory();

        if (response.success) {
            this.messageService.add({
                severity: 'success',
                summary: `Massen-${operationType} abgeschlossen`,
                detail: response.message
            });
        } else if (response.operationStatus === BulkOperationStatusEnum.partiallyCompleted) {
            this.messageService.add({
                severity: 'warn',
                summary: `Massen-${operationType} teilweise abgeschlossen`,
                detail: response.message
            });
        } else {
            this.messageService.add({
                severity: 'error',
                summary: `Massen-${operationType} fehlgeschlagen`,
                detail: response.message
            });
        }

        this.onOperationComplete.emit(response);
    }

    private handleOperationError(operationType: string): void {
        this.busy = false;
        this.messageService.add({
            severity: 'error',
            summary: 'Fehler',
            detail: `Massen-${operationType} konnte nicht ausgeführt werden`
        });
    }

    // Label helpers
    getOperationTypeLabel(type: BulkOperationTypeEnum): string {
        const labels: { [key: number]: string } = {
            [BulkOperationTypeEnum.statusChange]: 'Statusänderung',
            [BulkOperationTypeEnum.assignment]: 'Zuweisung',
            [BulkOperationTypeEnum.removal]: 'Entfernung',
            [BulkOperationTypeEnum.export]: 'Export',
            [BulkOperationTypeEnum.doNotContact]: 'Nicht kontaktieren'
        };
        return labels[type] || String(type);
    }

    getOperationStatusLabel(status: BulkOperationStatusEnum): string {
        const labels: { [key: number]: string } = {
            [BulkOperationStatusEnum.pending]: 'Ausstehend',
            [BulkOperationStatusEnum.processing]: 'In Bearbeitung',
            [BulkOperationStatusEnum.completed]: 'Abgeschlossen',
            [BulkOperationStatusEnum.failed]: 'Fehlgeschlagen',
            [BulkOperationStatusEnum.partiallyCompleted]: 'Teilweise abgeschlossen',
            [BulkOperationStatusEnum.cancelled]: 'Abgebrochen'
        };
        return labels[status] || String(status);
    }

    getOperationStatusSeverity(status: BulkOperationStatusEnum): string | any {
        const severities: { [key: number]: string } = {
            [BulkOperationStatusEnum.pending]: 'info',
            [BulkOperationStatusEnum.processing]: 'info',
            [BulkOperationStatusEnum.completed]: 'success',
            [BulkOperationStatusEnum.failed]: 'danger',
            [BulkOperationStatusEnum.partiallyCompleted]: 'warn',
            [BulkOperationStatusEnum.cancelled]: 'secondary'
        };
        return severities[status] || 'info';
    }

    getItemStatusLabel(status: BulkOperationItemStatusEnum): string {
        const labels: { [key: number]: string } = {
            [BulkOperationItemStatusEnum.pending]: 'Ausstehend',
            [BulkOperationItemStatusEnum.success]: 'Erfolgreich',
            [BulkOperationItemStatusEnum.failed]: 'Fehlgeschlagen',
            [BulkOperationItemStatusEnum.skipped]: 'Übersprungen'
        };
        return labels[status] || String(status);
    }

    getItemStatusSeverity(status: BulkOperationItemStatusEnum): string | any {
        const severities: { [key: number]: string } = {
            [BulkOperationItemStatusEnum.pending]: 'info',
            [BulkOperationItemStatusEnum.success]: 'success',
            [BulkOperationItemStatusEnum.failed]: 'danger',
            [BulkOperationItemStatusEnum.skipped]: 'secondary'
        };
        return severities[status] || 'info';
    }
}
